const STORAGE_KEY = "recently_accessed_policies";
const MAX_RECENT_POLICIES = 15;

export interface IRecentlyAccessedPolicy {
  customer_policy_id: number | string;
  customer_id: number | string;
  line_of_business: string;
  policy_number?: string;
  customer_name?: string;
  customer_code?: string;
  insurer?: string;
  accessed_at: number;
}

/**
 * Reads the list of recently accessed policies from localStorage,
 * ordered from most recently accessed to least.
 */
export const getRecentlyAccessedPolicies = (): IRecentlyAccessedPolicy[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as IRecentlyAccessedPolicy[];
  } catch {
    return [];
  }
};

/**
 * Records a policy as recently accessed. The most recent access is moved
 * to the front, duplicates (by customer_policy_id) are removed, and the list
 * is capped at MAX_RECENT_POLICIES entries.
 */
export const addRecentlyAccessedPolicy = (
  policy: Omit<IRecentlyAccessedPolicy, "accessed_at">
): void => {
  if (
    policy?.customer_policy_id === undefined ||
    policy?.customer_policy_id === null
  ) {
    return;
  }
  try {
    const existing = getRecentlyAccessedPolicies().filter(
      (p) =>
        String(p.customer_policy_id) !== String(policy.customer_policy_id)
    );
    const updated: IRecentlyAccessedPolicy[] = [
      { ...policy, accessed_at: Date.now() },
      ...existing,
    ].slice(0, MAX_RECENT_POLICIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // Ignore storage failures (e.g., quota exceeded, disabled storage)
  }
};

/**
 * Clears all recently accessed policies.
 */
export const clearRecentlyAccessedPolicies = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage failures
  }
};
